import React, { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  AddRounded,
  BusinessRounded,
  CancelRounded,
  CloseRounded,
  DeleteOutlineRounded,
  DescriptionRounded,
  FolderCopy,
  InfoOutlined,
  SaveRounded,
  SchoolRounded,
  SearchRounded,
  SubjectRounded,
  TitleRounded,
} from "@mui/icons-material";

import { useNavigate } from "react-router-dom";
import { api } from "@api/api";

import {
  botaoAdicionar,
  botaoCancelar,
  botaoSalvar,
} from "../../../estilos/botoes";

import { ajustarTamanhoFonteSre } from "../../../estilos/global";

// A classificação ainda não interfere nas regras de cadastro.
const EXIBIR_TIPO_INSTITUICAO = process.env.NODE_ENV !== "production";

const estiloSecao = {
  border: "1px solid #d7e7f7",
  borderRadius: 1.3,
  backgroundColor: "#fff",
  p: 1.4,
  boxShadow: "none",
};

const estiloCabecalhoTabela = {
  bgcolor: "#edf6fe",
  color: "#27456f",
  fontSize: ajustarTamanhoFonteSre("0.74rem"),
  fontWeight: 700,
  py: 0.8,
  borderBottom: "1px solid #d7e7f7",
};

function TituloSecao({ icon, titulo, subtitulo }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 0.95,
        mb: 1.1,
        bgcolor: "#f0f7ff",
        border: "1px solid #dceaf8",
        borderRadius: 1.1,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          display: "grid",
          placeItems: "center",
          bgcolor: "#e3f1ff",
          color: "#087cf0",
          borderRadius: 1,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: "#0767d8",
            fontSize: ajustarTamanhoFonteSre("0.86rem"),
            fontWeight: 700,
          }}
        >
          {titulo}
        </Typography>

        <Typography
          sx={{
            color: "#71849d",
            fontSize: ajustarTamanhoFonteSre("0.71rem"),
          }}
        >
          {subtitulo}
        </Typography>
      </Box>
    </Box>
  );
}

export default function CriarProcesso({
  onCancelar,
  onCriado,
  onAtualizado,
  modo = "criar",
  processoId = null,
} = {}) {
  const navigate = useNavigate();
  const editando = modo === "editar";

  const [original, setOriginal] = useState(null);
  const [carregando, setCarregando] = useState(editando);

  const [processo, setProcesso] = useState({
    nome: "",
    descricao: "",
    documentos: [],
    paraTodasEscolas: false,
  });

  // Não presumir categoria de processos antigos.
  const [tipoInstituicao, setTipoInstituicao] = useState(
    editando ? "" : "mantenedora",
  );

  const [abaCadastro, setAbaCadastro] = useState(0);
  const [confirmarSaida, setConfirmarSaida] = useState(false);
  const [documentosDisponiveis, setDocumentosDisponiveis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recarregarDocumentos, setRecarregarDocumentos] = useState(0);
  const [errors, setErrors] = useState({});
  const [errorType, setErrorType] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [modalDocumentosAberto, setModalDocumentosAberto] = useState(false);
  const [buscaModal, setBuscaModal] = useState("");
  const [buscaVinculados, setBuscaVinculados] = useState("");
  const [documentosMarcados, setDocumentosMarcados] = useState([]);

  // ====================================================
  // CARREGAMENTO
  // ====================================================

  useEffect(() => {
    let ativo = true;

    const buscar = async () => {
      try {
        setCarregando(true);

        const requisicoes = [
          fetch(api.adminListDocs(), {
            credentials: "include",
          }),
        ];

        if (editando) {
          if (!processoId) {
            throw new Error("ID do processo não informado.");
          }

          requisicoes.push(
            fetch(api.adminSearchProcess(processoId), {
              credentials: "include",
            }),
          );
        }

        const respostas = await Promise.all(requisicoes);

        for (const resposta of respostas) {
          if (!resposta.ok) {
            const erro = new Error(
              "Não foi possível consultar os dados do processo.",
            );
            erro.status = resposta.status;
            throw erro;
          }
        }

        const [listaDocumentos, dadosProcesso] = await Promise.all(
          respostas.map((resposta) => resposta.json()),
        );

        if (!Array.isArray(listaDocumentos)) {
          throw new Error("Resposta inválida da consulta de documentos.");
        }

        if (
          editando &&
          (!dadosProcesso ||
            dadosProcesso.error ||
            !Array.isArray(dadosProcesso.documentos))
        ) {
          throw new Error(
            dadosProcesso?.error || "Dados do processo inválidos.",
          );
        }

        if (!ativo) return;

        setDocumentosDisponiveis(listaDocumentos);

        if (editando) {
          const dados = {
            nome: String(dadosProcesso.nome ?? ""),
            descricao: String(dadosProcesso.descricao ?? ""),
            documentos: dadosProcesso.documentos.map((doc) => doc.id),
            paraTodasEscolas: dadosProcesso.para_todas_escolas,
          };

          if (
            dados.paraTodasEscolas === undefined ||
            dados.paraTodasEscolas === null
          ) {
            throw new Error(
              "O processo não retornou a configuração de aplicabilidade.",
            );
          }

          setProcesso(dados);
          setOriginal(dados);
        }

        setErrorType(null);
      } catch (erro) {
        console.error("Erro ao carregar cadastro de processo:", erro);

        if (ativo) {
          setErrorType(erro.status === 403 ? "permission" : "module");
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    };

    buscar();

    return () => {
      ativo = false;
    };
  }, [editando, processoId, recarregarDocumentos]);

  // ====================================================
  // CAMPOS
  // ====================================================

  const handleChange = ({ target: { name, value } }) => {
    setProcesso((atual) => ({
      ...atual,
      [name]: value,
    }));

    setErrors((atual) => ({
      ...atual,
      [name]: "",
    }));
  };

  const documentosPorId = new Map(
    documentosDisponiveis.map((doc) => [String(doc.id), doc]),
  );

  const idsVinculados = new Set(processo.documentos.map(String));

  const correspondeBusca = (doc, termo) =>
    `${doc.nome || ""} ${doc.descricao || ""}`
      .toLocaleLowerCase("pt-BR")
      .includes(termo.trim().toLocaleLowerCase("pt-BR"));

  const documentosVinculados = processo.documentos
    .map((id) => documentosPorId.get(String(id)))
    .filter(Boolean)
    .filter((doc) => correspondeBusca(doc, buscaVinculados));

  const documentosModal = documentosDisponiveis.filter((doc) =>
    correspondeBusca(doc, buscaModal),
  );

  const elegiveisVisiveis = documentosModal.filter(
    (doc) => !idsVinculados.has(String(doc.id)),
  );

  const estaMarcado = (id) =>
    documentosMarcados.some((item) => String(item) === String(id));

  const todosVisiveisMarcados =
    elegiveisVisiveis.length > 0 &&
    elegiveisVisiveis.every((doc) => estaMarcado(doc.id));

  const algumVisivelMarcado = elegiveisVisiveis.some((doc) =>
    estaMarcado(doc.id),
  );

  // ====================================================
  // SELEÇÃO DE DOCUMENTOS
  // ====================================================

  const abrirModalDocumentos = () => {
    setBuscaModal("");
    setDocumentosMarcados([]);
    setModalDocumentosAberto(true);
  };

  const alternarDocumentoMarcado = (id, marcado) => {
    setDocumentosMarcados((atual) =>
      marcado
        ? [...atual.filter((item) => String(item) !== String(id)), id]
        : atual.filter((item) => String(item) !== String(id)),
    );
  };

  const alternarTodosVisiveis = (marcado) => {
    const visiveis = new Set(elegiveisVisiveis.map((doc) => String(doc.id)));

    setDocumentosMarcados((atual) => {
      const outros = atual.filter((id) => !visiveis.has(String(id)));

      return marcado
        ? [...outros, ...elegiveisVisiveis.map((doc) => doc.id)]
        : outros;
    });
  };

  const adicionarDocumentosSelecionados = () => {
    setProcesso((atual) => {
      const existentes = new Set(atual.documentos.map(String));

      return {
        ...atual,
        documentos: [
          ...atual.documentos,
          ...documentosMarcados.filter((id) => !existentes.has(String(id))),
        ],
      };
    });

    setErrors((atual) => ({
      ...atual,
      documentos: "",
    }));

    setModalDocumentosAberto(false);
    setDocumentosMarcados([]);
  };

  const removerDocumento = (id) => {
    setProcesso((atual) => ({
      ...atual,
      documentos: atual.documentos.filter(
        (item) => String(item) !== String(id),
      ),
    }));
  };

  // ====================================================
  // ALTERAÇÕES E VALIDAÇÃO
  // ====================================================

  const idsIguais = (a, b) =>
    a.length === b.length &&
    a.every((id) => b.map(String).includes(String(id)));

  const dadosAlterados = Boolean(
    !editando ||
    (original &&
      (processo.nome !== original.nome ||
        processo.descricao !== original.descricao ||
        !idsIguais(processo.documentos, original.documentos))),
  );

  // ====================================================
  // SALVAR
  // ====================================================

  const handleSave = async () => {
    if (loading || carregando || errorType || (editando && !dadosAlterados)) {
      return;
    }

    const novosErros = {};

    if (!processo.nome.trim()) {
      novosErros.nome = "Nome do processo é obrigatório";
    }

    if (!processo.descricao.trim()) {
      novosErros.descricao = "Descrição é obrigatória";
    }

    if (!processo.documentos.length || processo.documentos.some((id) => !id)) {
      novosErros.documentos = "Adicione pelo menos um documento";
    }

    setErrors(novosErros);

    if (Object.keys(novosErros).length) {
      setAbaCadastro(novosErros.nome || novosErros.descricao ? 0 : 1);
      return;
    }

    try {
      setLoading(true);

      const resposta = await fetch(
        editando
          ? api.adminUpdateProcess(processoId)
          : api.adminCreateProcessType(),
        {
          credentials: "include",
          method: editando ? "PATCH" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(
            editando
              ? {
                  nome: processo.nome,
                  descricao: processo.descricao,
                  documentos: processo.documentos.map((id) => ({ id })),
                  para_todas_escolas: processo.paraTodasEscolas,
                }
              : {
                  nome: processo.nome,
                  descricao: processo.descricao,
                  documentos: processo.documentos,
                  paraTodasEscolas: processo.paraTodasEscolas,
                },
          ),
        },
      );

      if (!resposta.ok) {
        const dados = await resposta.json().catch(() => ({}));

        throw new Error(
          dados.error || dados.message || "Erro ao salvar processo",
        );
      }

      if (editando ? onAtualizado : onCriado) {
        if (editando) {
          onAtualizado();
        } else {
          onCriado();
        }
      } else {
        setSnackbar({
          open: true,
          message: editando
            ? "Processo atualizado com sucesso!"
            : "Processo criado com sucesso!",
          severity: "success",
        });

        setTimeout(() => navigate("/admin/processo"), 1500);
      }
    } catch (erro) {
      console.error("Erro ao salvar processo:", erro);

      setSnackbar({
        open: true,
        message: erro.message || "Erro ao salvar processo",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ====================================================
  // FECHAMENTO
  // ====================================================

  const possuiAlteracoes = editando
    ? dadosAlterados || tipoInstituicao !== ""
    : Boolean(
        processo.nome.trim() ||
        processo.descricao.trim() ||
        processo.documentos.length ||
        tipoInstituicao !== "mantenedora",
      );

  const fecharCadastro = () => {
    setConfirmarSaida(false);

    if (onCancelar) {
      onCancelar();
    } else {
      navigate("/admin/processo");
    }
  };

  const solicitarFechamento = () => {
    if (loading || modalDocumentosAberto || confirmarSaida) {
      return;
    }

    if (possuiAlteracoes) {
      setConfirmarSaida(true);
    } else {
      fecharCadastro();
    }
  };

  // ====================================================
  // TABELA DE DOCUMENTOS
  // ====================================================

  const documentosTabela = (
    <Box sx={{ ...estiloSecao, mb: 0 }}>
      <TituloSecao
        icon={<DescriptionRounded sx={{ fontSize: 19 }} />}
        titulo="Documentos necessários"
        subtitulo="Adicione os documentos que serão exigidos neste processo."
      />

      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 1.1,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Buscar documento..."
          value={buscaVinculados}
          onChange={(e) => setBuscaVinculados(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded
                  sx={{
                    fontSize: 17,
                    color: "#607995",
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{ flex: "1 1 240px" }}
        />

        <Button
          size="small"
          variant="contained"
          startIcon={<AddRounded />}
          onClick={abrirModalDocumentos}
          disabled={loading}
          sx={botaoAdicionar}
        >
          Adicionar documento
        </Button>
      </Box>

      <TableContainer
        sx={{
          border: "1px solid #d7e7f7",
          borderRadius: 1.1,
          maxHeight: 258,
          overflowY: "auto",
        }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: 420 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={estiloCabecalhoTabela}>Documento</TableCell>

              <TableCell
                align="right"
                sx={{
                  ...estiloCabecalhoTabela,
                  width: 106,
                }}
              >
                Ações
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {documentosVinculados.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{
                    py: 2.6,
                    color: "#71849d",
                    fontSize: ajustarTamanhoFonteSre("0.77rem"),
                  }}
                >
                  <DescriptionRounded
                    sx={{
                      display: "block",
                      mx: "auto",
                      mb: 0.4,
                      opacity: 0.45,
                    }}
                  />

                  {processo.documentos.length === 0
                    ? "Nenhum documento adicionado."
                    : "Nenhum documento corresponde à busca."}
                </TableCell>
              </TableRow>
            ) : (
              documentosVinculados.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell sx={{ py: 0.65 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          p: 0.55,
                          color: "#087cf0",
                          bgcolor: "#eaf4ff",
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 0.7,
                        }}
                      >
                        <DescriptionRounded sx={{ fontSize: 17 }} />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: "#173f72",
                            fontWeight: 700,
                            fontSize: ajustarTamanhoFonteSre("0.76rem"),
                          }}
                        >
                          {doc.nome}
                        </Typography>

                        <Typography
                          sx={{
                            color: "#71849d",
                            fontSize: ajustarTamanhoFonteSre("0.69rem"),
                            overflowWrap: "anywhere",
                          }}
                        >
                          {doc.descricao || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell align="right" sx={{ py: 0.65 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutlineRounded sx={{ fontSize: 15 }} />}
                      onClick={() => removerDocumento(doc.id)}
                      sx={{
                        textTransform: "none",
                        fontSize: ajustarTamanhoFonteSre("0.72rem"),
                        minWidth: 0,
                        py: 0.2,
                      }}
                    >
                      Remover
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {errors.documentos && (
        <Typography
          role="alert"
          color="error"
          sx={{
            fontSize: ajustarTamanhoFonteSre("0.74rem"),
            mt: 0.65,
          }}
        >
          {errors.documentos}
        </Typography>
      )}

      <Typography
        sx={{
          mt: 0.8,
          color: "#71849d",
          fontSize: ajustarTamanhoFonteSre("0.69rem"),
        }}
      >
        <InfoOutlined
          sx={{
            fontSize: 13,
            verticalAlign: "middle",
            mr: 0.5,
          }}
        />
        Quando houver mais documentos, a lista terá rolagem interna.
      </Typography>
    </Box>
  );

  return (
    <>
      {/* MODAL PRINCIPAL */}

      <Dialog
        open
        fullWidth
        maxWidth="sm"
        onClose={solicitarFechamento}
        aria-labelledby="titulo-criar-processo"
        PaperProps={{
          sx: {
            width: "calc(100% - 20px)",
            maxWidth: "670px !important",
            maxHeight: "calc(100% - 24px)",
            borderRadius: 1.7,
            border: "1px solid #d7e7f7",
            borderTop: "4px solid #087cf0",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 1.7,
            py: 1.05,
            bgcolor: "#f9fcff",
            borderBottom: "1px solid #e5edf5",
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              display: {
                xs: "none",
                sm: "grid",
              },
              placeItems: "center",
              width: 36,
              height: 36,
              bgcolor: "#eaf4ff",
              color: "#087cf0",
              borderRadius: 1,
            }}
          >
            <FolderCopy sx={{ fontSize: 22 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id="titulo-criar-processo"
              component="h2"
              sx={{
                color: "#0767d8",
                fontSize: ajustarTamanhoFonteSre("1.12rem"),
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              {editando ? "Editar Processo" : "Criar Novo Processo"}
            </Typography>

            <Typography
              sx={{
                color: "#71849d",
                fontSize: ajustarTamanhoFonteSre("0.71rem"),
                display: {
                  xs: "none",
                  sm: "block",
                },
              }}
            >
              {editando
                ? "Atualize as informações e os documentos do processo."
                : "Preencha as informações principais do processo."}
            </Typography>
          </Box>

          <IconButton
            size="small"
            aria-label="Fechar cadastro"
            disabled={loading}
            onClick={solicitarFechamento}
          >
            <CloseRounded sx={{ fontSize: 19 }} />
          </IconButton>
        </DialogTitle>

        {/* ABAS */}

        <Tabs
          value={abaCadastro}
          onChange={(_, novaAba) => setAbaCadastro(novaAba)}
          aria-label="Abas do cadastro de processo"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            px: 1.8,
            borderBottom: "1px solid #e5edf5",
            "& .MuiTab-root": {
              minHeight: 40,
              textTransform: "none",
              fontSize: ajustarTamanhoFonteSre("0.73rem"),
              py: 0.6,
              px: 1.3,
            },
            "& .MuiTabs-indicator": {
              bgcolor: "#087cf0",
              height: 2,
            },
          }}
        >
          <Tab
            icon={<FolderCopy sx={{ fontSize: 15 }} />}
            iconPosition="start"
            label="Dados do processo"
            id="aba-criar-dados"
            aria-controls="painel-criar-dados"
          />

          <Tab
            icon={<DescriptionRounded sx={{ fontSize: 15 }} />}
            iconPosition="start"
            label="Documentos necessários"
            id="aba-criar-documentos"
            aria-controls="painel-criar-documentos"
          />
        </Tabs>

        <DialogContent
          sx={{
            px: 1.8,
            py: 1.2,
            bgcolor: "#fbfdff",
          }}
        >
          {carregando ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 5,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : errorType ? (
            <Alert
              severity={errorType === "permission" ? "error" : "warning"}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setErrorType(null);
                    setRecarregarDocumentos((n) => n + 1);
                  }}
                >
                  Tentar novamente
                </Button>
              }
            >
              {errorType === "permission"
                ? "Sem permissão para consultar os documentos."
                : "Não foi possível carregar os documentos."}
            </Alert>
          ) : abaCadastro === 0 ? (
            /* ABA 1 */
            <Box
              role="tabpanel"
              id="painel-criar-dados"
              aria-labelledby="aba-criar-dados"
            >
              <TituloSecao
                icon={<DescriptionRounded sx={{ fontSize: 19 }} />}
                titulo="Dados do processo"
                subtitulo="Preencha as informações principais do processo."
              />

              {EXIBIR_TIPO_INSTITUICAO && (
                <Box sx={{ mb: 1.4 }}>
                  <Typography
                    sx={{
                      color: "#27456f",
                      fontSize: ajustarTamanhoFonteSre("0.75rem"),
                      fontWeight: 700,
                      mb: 0.55,
                    }}
                  >
                    Tipo de instituição{" "}
                    <Tooltip title="Classificação experimental: ainda não é salva nem interfere no cadastro.">
                      <InfoOutlined
                        sx={{
                          color: "#087cf0",
                          fontSize: 14,
                          verticalAlign: "middle",
                        }}
                      />
                    </Tooltip>
                  </Typography>

                  {editando && !tipoInstituicao && (
                    <Typography
                      sx={{
                        color: "#71849d",
                        fontSize: ajustarTamanhoFonteSre("0.69rem"),
                        mb: 0.5,
                      }}
                    >
                      Categoria ainda não registrada para este processo; escolha
                      apenas para visualizar.
                    </Typography>
                  )}

                  <RadioGroup
                    row
                    value={tipoInstituicao}
                    onChange={(e) => setTipoInstituicao(e.target.value)}
                    aria-label="Tipo de instituição (pré-visualização)"
                    sx={{ gap: 0.9 }}
                  >
                    {[
                      {
                        valor: "mantenedora",
                        nome: "Mantenedora",
                        Icone: BusinessRounded,
                        legenda: "Processo vinculado a uma mantenedora.",
                      },
                      {
                        valor: "escola",
                        nome: "Escola",
                        Icone: SchoolRounded,
                        legenda: "Processo vinculado a uma escola.",
                      },
                    ].map(({ valor, nome, Icone, legenda }) => (
                      <FormControlLabel
                        key={valor}
                        value={valor}
                        control={<Radio size="small" />}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              minWidth: 0,
                            }}
                          >
                            <Box
                              sx={{
                                color: "#087cf0",
                                bgcolor: "#eaf4ff",
                                p: 0.4,
                                borderRadius: 0.6,
                                display: "grid",
                                placeItems: "center",
                              }}
                            >
                              <Icone
                                sx={{
                                  fontSize: 19,
                                }}
                              />
                            </Box>

                            <Box>
                              <Typography
                                sx={{
                                  color: "#0767d8",
                                  fontSize: ajustarTamanhoFonteSre("0.75rem"),
                                  fontWeight: 700,
                                }}
                              >
                                {nome}
                              </Typography>

                              <Typography
                                sx={{
                                  color: "#71849d",
                                  fontSize: ajustarTamanhoFonteSre("0.66rem"),
                                }}
                              >
                                {legenda}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        sx={{
                          m: 0,
                          px: 0.8,
                          py: 0.55,
                          borderRadius: 1,
                          border:
                            tipoInstituicao === valor
                              ? "1px solid #087cf0"
                              : "1px solid #d7e7f7",
                          bgcolor:
                            tipoInstituicao === valor ? "#eef7ff" : "#fff",
                          flex: "1 1 225px",
                          minWidth: 0,
                          maxWidth: "100%",
                        }}
                      />
                    ))}
                  </RadioGroup>
                </Box>
              )}

              {/* NOME */}

              <Box sx={{ mb: 1.2 }}>
                <Typography
                  component="label"
                  htmlFor="nome-processo"
                  sx={{
                    display: "block",
                    color: "#27456f",
                    fontSize: ajustarTamanhoFonteSre("0.76rem"),
                    fontWeight: 700,
                    mb: 0.45,
                  }}
                >
                  Nome do processo{" "}
                  <Box component="span" sx={{ color: "#e53935" }}>
                    *
                  </Box>
                </Typography>

                <TextField
                  id="nome-processo"
                  fullWidth
                  size="small"
                  name="nome"
                  placeholder="Digite o nome do processo..."
                  value={processo.nome}
                  onChange={handleChange}
                  error={!!errors.nome}
                  helperText={errors.nome}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleRounded
                          sx={{
                            fontSize: 17,
                            color: "#607995",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* DESCRIÇÃO */}

              <Box>
                <Typography
                  component="label"
                  htmlFor="descricao-processo"
                  sx={{
                    display: "block",
                    color: "#27456f",
                    fontSize: ajustarTamanhoFonteSre("0.76rem"),
                    fontWeight: 700,
                    mb: 0.45,
                  }}
                >
                  Descrição{" "}
                  <Box component="span" sx={{ color: "#e53935" }}>
                    *
                  </Box>
                </Typography>

                <TextField
                  id="descricao-processo"
                  fullWidth
                  size="small"
                  name="descricao"
                  placeholder="Descreva o processo em detalhes..."
                  multiline
                  minRows={3}
                  value={processo.descricao}
                  onChange={handleChange}
                  error={!!errors.descricao}
                  helperText={errors.descricao}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{
                          alignSelf: "flex-start",
                          mt: 0.4,
                        }}
                      >
                        <SubjectRounded
                          sx={{
                            fontSize: 17,
                            color: "#607995",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          ) : (
            /* ABA 2 */
            <Box
              role="tabpanel"
              id="painel-criar-documentos"
              aria-labelledby="aba-criar-documentos"
            >
              <Box
                sx={{
                  ...estiloSecao,
                  mb: 1,
                }}
              >
                <TituloSecao
                  icon={<FolderCopy sx={{ fontSize: 19 }} />}
                  titulo="Resumo do processo"
                  subtitulo="Confira abaixo as informações do processo preenchidas na etapa anterior."
                />

                <Box
                  sx={{
                    display: "grid",
                    gap: 1.1,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: EXIBIR_TIPO_INSTITUICAO
                        ? "1fr 1.45fr 0.9fr"
                        : "1fr 2fr",
                    },
                    p: 1.1,
                    bgcolor: "#f2f8fe",
                    border: "1px solid #d7e7f7",
                    borderRadius: 0.8,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: "#71849d",
                        fontSize: ajustarTamanhoFonteSre("0.67rem"),
                      }}
                    >
                      Nome do processo
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                      }}
                    >
                      <TitleRounded
                        sx={{
                          color: "#607995",
                          fontSize: 16,
                          flexShrink: 0,
                        }}
                      />

                      <Typography
                        sx={{
                          color: "#173f72",
                          fontWeight: 700,
                          fontSize: ajustarTamanhoFonteSre("0.74rem"),
                          overflowWrap: "anywhere",
                        }}
                      >
                        {processo.nome || "Não informado"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        color: "#71849d",
                        fontSize: ajustarTamanhoFonteSre("0.67rem"),
                      }}
                    >
                      Descrição
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.6,
                      }}
                    >
                      <SubjectRounded
                        sx={{
                          color: "#607995",
                          fontSize: 16,
                          flexShrink: 0,
                          mt: 0.1,
                        }}
                      />

                      <Typography
                        sx={{
                          color: "#40546d",
                          fontSize: ajustarTamanhoFonteSre("0.73rem"),
                          overflowWrap: "anywhere",
                        }}
                      >
                        {processo.descricao || "Não informada"}
                      </Typography>
                    </Box>
                  </Box>

                  {EXIBIR_TIPO_INSTITUICAO && (
                    <Box>
                      <Typography
                        sx={{
                          color: "#71849d",
                          fontSize: ajustarTamanhoFonteSre("0.67rem"),
                        }}
                      >
                        Tipo de instituição
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.6,
                        }}
                      >
                        {tipoInstituicao === "mantenedora" ? (
                          <BusinessRounded
                            sx={{
                              color: "#087cf0",
                              fontSize: 17,
                              flexShrink: 0,
                            }}
                          />
                        ) : tipoInstituicao === "escola" ? (
                          <SchoolRounded
                            sx={{
                              color: "#087cf0",
                              fontSize: 17,
                              flexShrink: 0,
                            }}
                          />
                        ) : null}

                        <Typography
                          sx={{
                            color: "#173f72",
                            fontWeight: 700,
                            fontSize: ajustarTamanhoFonteSre("0.74rem"),
                          }}
                        >
                          {tipoInstituicao === "mantenedora"
                            ? "Mantenedora"
                            : tipoInstituicao === "escola"
                              ? "Escola"
                              : "Não definida"}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {documentosTabela}
            </Box>
          )}
        </DialogContent>

        {/* RODAPÉ */}

        <DialogActions
          sx={{
            px: 1.8,
            py: 0.95,
            borderTop: "1px solid #e5edf5",
            justifyContent: "space-between",
            bgcolor: "#fff",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CancelRounded />}
            onClick={solicitarFechamento}
            disabled={loading}
            sx={botaoCancelar}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={15} color="inherit" />
              ) : (
                <SaveRounded />
              )
            }
            onClick={handleSave}
            disabled={
              loading ||
              carregando ||
              Boolean(errorType) ||
              (editando && !dadosAlterados)
            }
            sx={botaoSalvar}
          >
            {loading
              ? "Salvando..."
              : editando
                ? "Salvar alterações"
                : "Criar processo"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL ADICIONAR DOCUMENTOS */}

      <Dialog
        open={modalDocumentosAberto}
        onClose={() => setModalDocumentosAberto(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 1.6,
            borderTop: "4px solid #087cf0",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2,
            py: 1.35,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e5edf5",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <DescriptionRounded
              sx={{
                color: "#087cf0",
                fontSize: 26,
              }}
            />

            <Box>
              <Typography
                sx={{
                  color: "#0767d8",
                  fontWeight: 700,
                  fontSize: ajustarTamanhoFonteSre("0.96rem"),
                }}
              >
                Adicionar documentos
              </Typography>

              <Typography
                sx={{
                  color: "#71849d",
                  fontSize: ajustarTamanhoFonteSre("0.71rem"),
                }}
              >
                Selecione os documentos que farão parte deste processo.
              </Typography>
            </Box>
          </Box>

          <IconButton
            size="small"
            aria-label="Fechar"
            onClick={() => setModalDocumentosAberto(false)}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1.4 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar documento..."
            value={buscaModal}
            onChange={(e) => setBuscaModal(e.target.value)}
            sx={{ mb: 1.4 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    sx={{
                      color: "#607995",
                      fontSize: 17,
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer
            sx={{
              maxHeight: 340,
              border: "1px solid #d7e7f7",
              borderRadius: 1,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={estiloCabecalhoTabela}>
                    <Checkbox
                      size="small"
                      checked={todosVisiveisMarcados}
                      indeterminate={
                        algumVisivelMarcado && !todosVisiveisMarcados
                      }
                      disabled={!elegiveisVisiveis.length}
                      inputProps={{
                        "aria-label":
                          "Selecionar documentos disponíveis nesta busca",
                      }}
                      onChange={(e) => alternarTodosVisiveis(e.target.checked)}
                    />
                  </TableCell>

                  <TableCell sx={estiloCabecalhoTabela}>Documento</TableCell>

                  <TableCell sx={estiloCabecalhoTabela}>Descrição</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {documentosModal.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        py: 3,
                        color: "#71849d",
                      }}
                    >
                      Nenhum documento encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  documentosModal.map((doc) => {
                    const jaVinculado = idsVinculados.has(String(doc.id));
                    const marcado = jaVinculado || estaMarcado(doc.id);

                    return (
                      <TableRow key={doc.id} hover selected={marcado}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={marcado}
                            disabled={jaVinculado}
                            inputProps={{
                              "aria-label": `Selecionar documento ${doc.nome}`,
                            }}
                            onChange={(e) =>
                              alternarDocumentoMarcado(doc.id, e.target.checked)
                            }
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            color: "#173f72",
                            fontSize: ajustarTamanhoFonteSre("0.77rem"),
                            fontWeight: 600,
                          }}
                        >
                          {doc.nome}
                          {jaVinculado && (
                            <Typography
                              component="span"
                              sx={{
                                ml: 0.5,
                                color: "#60748d",
                                fontSize: ajustarTamanhoFonteSre("0.68rem"),
                              }}
                            >
                              (já adicionado)
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell
                          sx={{
                            color: "#60748d",
                            fontSize: ajustarTamanhoFonteSre("0.72rem"),
                          }}
                        >
                          {doc.descricao || "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.1,
            borderTop: "1px solid #e5edf5",
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CancelRounded />}
            sx={botaoCancelar}
            onClick={() => setModalDocumentosAberto(false)}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            startIcon={<AddRounded />}
            sx={botaoAdicionar}
            disabled={!documentosMarcados.length}
            onClick={adicionarDocumentosSelecionados}
          >
            Adicionar selecionados ({documentosMarcados.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRMAÇÃO DE DESCARTE */}

      <Dialog
        open={confirmarSaida}
        onClose={() => setConfirmarSaida(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Descartar alterações?</DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: ajustarTamanhoFonteSre("0.84rem") }}>
            Os dados preenchidos e os documentos selecionados não serão salvos.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmarSaida(false)} sx={botaoCancelar}>
            Continuar editando
          </Button>

          <Button variant="contained" color="error" onClick={fecharCadastro}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

      {/* FEEDBACK */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbar((atual) => ({
            ...atual,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          onClose={() =>
            setSnackbar((atual) => ({
              ...atual,
              open: false,
            }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
